import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, useTheme, alpha } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import config from './config';

const ImageSlider = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  const [sliderImages, setSliderImages] = useState([]);
  const [current, setCurrent] = useState(0);
  const [mainPages, setMainPages] = useState([]);

  // Fetch slider images and main pages
  useEffect(() => {
    // Fetch slider images
    fetch(`${config.API_BASE_URL}/RestaurantInfo/slider`)
      .then(res => res.json())
      .then(data => setSliderImages(data))
      .catch(() => setSliderImages([]));

    // Fetch main pages for button labels
    fetch(`${config.API_BASE_URL}/main-pages`)
      .then(res => res.json())
      .then(data => setMainPages(data))
      .catch(() => setMainPages([]));
  }, []);

  // Navigation function
  const nextImage = () => setCurrent((current + 1) % sliderImages.length);

  // Auto-slide every 10 seconds
  useEffect(() => {
    if (sliderImages.length === 0) return;
    const timer = setInterval(() => {
      setCurrent((c) => (c + 1) % sliderImages.length);
    }, 10000);
    return () => clearInterval(timer);
  }, [sliderImages]);

  return (
    <Box 
      onClick={(e) => {
        // Prevent slide if clicking on button
        if (e.target.closest('button')) return;
        nextImage();
      }}
      sx={{
        width: '100%',
        height: { xs: 500, md: 650 },
        position: 'relative',
        overflow: 'hidden',
        color: 'primary.contrastText',
        mb: 0,
        mt: { xs: '-80px', sm: '-100px' },
        boxShadow: `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
        cursor: 'pointer'
      }}
    >
      <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
        {/* Slider Images */}
        {sliderImages.map((img, index) => (
          <Box
            key={index}
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: current === index ? 1 : 0,
              transition: 'opacity 1s ease-in-out',
              zIndex: current === index ? 1 : 0,
              pointerEvents: current === index ? 'auto' : 'none',
            }}
          >
            {/* Background Image */}
            <Box 
              sx={{
                position: 'absolute',
                width: '100%',
                height: '100%',
                backgroundImage: `url(${img.ImagePath})`,
                backgroundPosition: 'center',
                backgroundSize: 'cover',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  background: `linear-gradient(to bottom, ${alpha(theme.palette.common.black, 0.1)} 0%, ${alpha(theme.palette.common.black, 0.6)} 100%)`,
                }
              }} 
            />

            {/* Content */}
            <Box 
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                px: 3,
                textAlign: 'center',
                zIndex: 1,
              }}
            >
              <Typography 
                variant="h2" 
                sx={{ 
                  fontWeight: 900, 
                  mb: 2, 
                  textShadow: '0 4px 12px rgba(0,0,0,0.5)',
                  fontSize: { xs: '2.5rem', md: '4rem' },
                  color: 'custom.white',
                  transform: current === index ? 'translateY(0)' : 'translateY(30px)',
                  opacity: current === index ? 1 : 0,
                  transition: 'all 0.8s ease-out 0.2s'
                }}
              >
                {img.Title}
              </Typography>
              
              <Typography 
                variant="h5" 
                sx={{ 
                  mb: 4, 
                  textShadow: '0 2px 8px rgba(0,0,0,0.5)',
                  fontWeight: 500,
                  maxWidth: 600,
                  color: 'custom.white',
                  transform: current === index ? 'translateY(0)' : 'translateY(30px)',
                  opacity: current === index ? 1 : 0,
                  transition: 'all 0.8s ease-out 0.4s'
                }}
              >
                {img.Subtitle}
              </Typography>
              
              {img.Action && (
                <Button
                  variant="contained"
                  size="large"
                  sx={{ 
                    fontSize: '1.2rem', 
                    px: 6, 
                    py: 1.5, 
                    borderRadius: 50, 
                    bgcolor: 'primary.main', 
                    color: 'custom.white', 
                    '&:hover': { bgcolor: 'primary.dark' },
                    transform: current === index ? 'translateY(0)' : 'translateY(30px)',
                    opacity: current === index ? 1 : 0,
                    transition: 'all 0.8s ease-out 0.6s'
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    navigate(img.Action);
                  }}
                >
                  {(() => {
                    const pageName = mainPages.find(p => p.PagePath === img.Action)?.PageName;
                    return pageName ? t(`pages.${pageName}`) : img.Action;
                  })()}
                </Button>
              )}
            </Box>
          </Box>
        ))}
        
        {/* Indicators */}
        <Box 
          sx={{ 
            position: 'absolute', 
            bottom: 40, 
            left: '50%', 
            transform: 'translateX(-50%)', 
            display: 'flex', 
            gap: 1.5, 
            zIndex: 2 
          }}
        >
          {sliderImages.map((_, index) => (
            <Box
              key={index}
              onClick={(e) => {
                e.stopPropagation();
                setCurrent(index);
              }}
              sx={{
                width: current === index ? 60 : 30,
                height: 6,
                bgcolor: current === index ? 'primary.main' : alpha(theme.palette.common.white, 0.5),
                borderRadius: 3,
                transition: 'all 0.3s ease',
                cursor: 'pointer',
                boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                '&:hover': {
                  bgcolor: current === index ? 'primary.main' : alpha(theme.palette.common.white, 0.8),
                }
              }}
            />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default ImageSlider;
